import React, { useState, useEffect, useRef, useCallback } from 'react';
import FlerpieAvatar from './FlerpieAvatar';
import FlerpieFeedbackPanel from './FlerpieFeedbackPanel';
import FlerpieRumorMillPanel from './FlerpieRumorMillPanel';

const EMOTIONS = [
  'neutral', 'steady', 'laughing', 'thinking', 'excited', 'concerned',
  'confused', 'proud', 'surprised', 'error', 'greeting',
];

const SIZES = [48, 72, 120];

const CYCLE_SPEEDS = [
  { label: '1s', ms: 1000 },
  { label: '2s', ms: 2000 },
  { label: '3s', ms: 3000 },
];

const EMOTION_COLORS = {
  neutral:   '#888',
  steady:    '#a8c070',
  laughing:  '#f0c040',
  thinking:  '#6ba3d6',
  excited:   '#f5c542',
  concerned: '#e08a5e',
  confused:  '#b07dd6',
  proud:     '#5ec269',
  surprised: '#e8d44d',
  error:     '#d65c5c',
  greeting:  '#5cc2b5',
};

const FlerpieDebugDashboard = () => {
  const [activeEmotion, setActiveEmotion] = useState('neutral');
  const [bgColor, setBgColor] = useState('#1a1a1a');
  const [showAll, setShowAll] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showRumors, setShowRumors] = useState(false);
  const [cycling, setCycling] = useState(false);
  const [cycleSpeed, setCycleSpeed] = useState(2000);
  const [showGuides, setShowGuides] = useState(true);
  const cycleIndex = useRef(0);
  const cycleTimer = useRef(null);

  const stepEmotion = useCallback(() => {
    cycleIndex.current = (cycleIndex.current + 1) % EMOTIONS.length;
    setActiveEmotion(EMOTIONS[cycleIndex.current]);
  }, []);

  // Auto-cycle
  useEffect(() => {
    if (!cycling) { clearInterval(cycleTimer.current); return; }
    cycleTimer.current = setInterval(stepEmotion, cycleSpeed);
    return () => clearInterval(cycleTimer.current);
  }, [cycling, cycleSpeed, stepEmotion]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#111', color: '#ccc',
      overflow: 'auto', fontFamily: 'monospace',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid #333',
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#FFCC33' }}>
          Flerpie Debug Dashboard
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <span style={{ color: '#888' }}>BG</span>
          <input
            type="color"
            value={bgColor}
            onChange={(e) => setBgColor(e.target.value)}
            style={{ width: 28, height: 22, border: 'none', cursor: 'pointer' }}
          />
        </label>
        <button
          onClick={() => setShowAll((v) => !v)}
          style={{
            padding: '4px 12px', fontSize: 12,
            background: showAll ? 'rgba(255,204,51,0.15)' : '#333',
            color: showAll ? '#FFCC33' : '#888',
            border: '1px solid #444', borderRadius: 6, cursor: 'pointer',
          }}
        >
          {showAll ? 'Grid View' : 'Single View'}
        </button>
        <button
          onClick={() => { setShowFeedback((v) => !v); setShowRumors(false); }}
          style={{
            padding: '4px 12px', fontSize: 12,
            background: showFeedback ? 'rgba(255,204,51,0.15)' : '#333',
            color: showFeedback ? '#FFCC33' : '#888',
            border: '1px solid #444', borderRadius: 6, cursor: 'pointer',
          }}
        >
          Feedback
        </button>
        <button
          onClick={() => { setShowRumors((v) => !v); setShowFeedback(false); }}
          style={{
            padding: '4px 12px', fontSize: 12,
            background: showRumors ? 'rgba(255,204,51,0.15)' : '#333',
            color: showRumors ? '#FFCC33' : '#888',
            border: '1px solid #444', borderRadius: 6, cursor: 'pointer',
          }}
        >
          Rumor mill
        </button>
      </div>

      {showFeedback ? (
        <FlerpieFeedbackPanel />
      ) : showRumors ? (
        <FlerpieRumorMillPanel />
      ) : showAll ? (
        /* ── Grid: all emotions at all sizes ── */
        <div style={{ padding: 24 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${EMOTIONS.length}, 1fr)`,
            gap: 8,
          }}>
            {EMOTIONS.map((emotion) => (
              <div key={emotion} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
                padding: 16, borderRadius: 12,
                background: bgColor,
                border: `1px solid ${EMOTION_COLORS[emotion]}33`,
              }}>
                <span style={{
                  fontSize: 10, fontWeight: 600, letterSpacing: 1,
                  color: EMOTION_COLORS[emotion], textTransform: 'uppercase',
                }}>
                  {emotion}
                </span>
                {SIZES.map((size) => (
                  <div key={size} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}>
                    <FlerpieAvatar emotion={emotion} size={size} />
                    <span style={{ fontSize: 9, color: '#555' }}>{size}px</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* ── Transition Tester ── */
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '32px 24px', gap: 24,
        }}>
          {/* Avatar with crosshair alignment guides */}
          <div style={{
            position: 'relative',
            background: bgColor, borderRadius: 24, padding: 40,
            border: `1px solid ${EMOTION_COLORS[activeEmotion]}22`,
            transition: 'border-color 0.3s',
          }}>
            <FlerpieAvatar emotion={activeEmotion} size={200} />
            {showGuides && (
              <>
                {/* Vertical center line */}
                <div style={{
                  position: 'absolute', top: 0, bottom: 0, left: '50%',
                  width: 1, background: 'rgba(255,0,0,0.25)', pointerEvents: 'none',
                }} />
                {/* Horizontal center line */}
                <div style={{
                  position: 'absolute', left: 0, right: 0, top: '50%',
                  height: 1, background: 'rgba(255,0,0,0.25)', pointerEvents: 'none',
                }} />
                {/* Bounding box */}
                <div style={{
                  position: 'absolute', inset: 40,
                  border: '1px dashed rgba(255,255,255,0.08)', pointerEvents: 'none',
                  borderRadius: 4,
                }} />
              </>
            )}
          </div>

          {/* Emotion label */}
          <span style={{
            fontSize: 24, fontWeight: 700, letterSpacing: 2,
            color: EMOTION_COLORS[activeEmotion],
            textTransform: 'uppercase', transition: 'color 0.3s',
          }}>
            {activeEmotion}
          </span>

          {/* Cycle controls */}
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            <button
              onClick={stepEmotion}
              style={{
                padding: '8px 16px', fontSize: 12, fontFamily: 'monospace',
                background: '#222', color: '#ccc',
                border: '1px solid #444', borderRadius: 8, cursor: 'pointer',
              }}
            >
              Step &rarr;
            </button>
            <button
              onClick={() => { setCycling((v) => !v); }}
              style={{
                padding: '8px 16px', fontSize: 12, fontFamily: 'monospace',
                background: cycling ? 'rgba(255,80,80,0.2)' : 'rgba(80,255,80,0.15)',
                color: cycling ? '#ff6b6b' : '#6bff6b',
                border: `1px solid ${cycling ? '#ff6b6b44' : '#6bff6b44'}`,
                borderRadius: 8, cursor: 'pointer',
              }}
            >
              {cycling ? 'Stop' : 'Auto-Cycle'}
            </button>
            {/* Speed selector */}
            {CYCLE_SPEEDS.map(({ label, ms }) => (
              <button
                key={ms}
                onClick={() => setCycleSpeed(ms)}
                style={{
                  padding: '4px 10px', fontSize: 11, fontFamily: 'monospace',
                  background: cycleSpeed === ms ? 'rgba(255,204,51,0.15)' : '#1a1a1a',
                  color: cycleSpeed === ms ? '#FFCC33' : '#555',
                  border: `1px solid ${cycleSpeed === ms ? '#FFCC3344' : '#333'}`,
                  borderRadius: 6, cursor: 'pointer',
                }}
              >
                {label}
              </button>
            ))}
            <button
              onClick={() => setShowGuides((v) => !v)}
              style={{
                padding: '4px 10px', fontSize: 11, fontFamily: 'monospace',
                background: showGuides ? 'rgba(255,0,0,0.1)' : '#1a1a1a',
                color: showGuides ? '#ff6666' : '#555',
                border: `1px solid ${showGuides ? '#ff666644' : '#333'}`,
                borderRadius: 6, cursor: 'pointer',
              }}
            >
              Guides
            </button>
          </div>

          {/* Manual emotion picker */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center',
          }}>
            {EMOTIONS.map((emotion) => (
              <button
                key={emotion}
                onClick={() => { setCycling(false); cycleIndex.current = EMOTIONS.indexOf(emotion); setActiveEmotion(emotion); }}
                style={{
                  padding: '8px 16px', fontSize: 12, fontFamily: 'monospace',
                  background: activeEmotion === emotion
                    ? `${EMOTION_COLORS[emotion]}33` : '#222',
                  color: activeEmotion === emotion
                    ? EMOTION_COLORS[emotion] : '#666',
                  border: `1px solid ${activeEmotion === emotion
                    ? EMOTION_COLORS[emotion] : '#333'}`,
                  borderRadius: 8, cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {emotion}
              </button>
            ))}
          </div>

          {/* Size row */}
          <div style={{ display: 'flex', gap: 24, alignItems: 'end' }}>
            {[32, 48, 64, 72, 96].map((size) => (
              <div key={size} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                background: bgColor, borderRadius: 12, padding: 12,
              }}>
                <FlerpieAvatar emotion={activeEmotion} size={size} />
                <span style={{ fontSize: 9, color: '#555' }}>{size}px</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlerpieDebugDashboard;
